/**
 * PermissionContext — operation-based permission system for the frontend.
 *
 * Fetches the authenticated user's effective permissions from
 * GET /api/permissions/effective and exposes a single hook:
 *
 *   const { canDo, permissions, loading } = usePermissions();
 *   {canDo('stations', 'update') && <button>Edit</button>}
 *
 * Replaces useRoles, useIsAdmin, and extractUserInfo for permission checks.
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { isDevMode } from '../services/configService';
import { getBackendUrl } from '../services/configService';

/** feature → operation[] */
type EffectivePermissions = Record<string, string[]>;

interface PermissionContextValue {
  /** Check if the current user can perform an operation on a feature */
  canDo: (feature: string, operation: string) => boolean;
  /** True if the current user has the admin role (identity check, not permission) */
  isAdmin: boolean;
  /** Raw effective permissions: feature → operations[] */
  permissions: EffectivePermissions;
  /** True while the initial fetch is in progress */
  loading: boolean;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

async function fetchEffectivePermissions(): Promise<EffectivePermissions> {
  const baseUrl = getBackendUrl();
  if (!baseUrl) {
    throw new Error('BACKEND_URL not configured in runtime config');
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('access_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${baseUrl}/api/permissions/effective`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });

  if (!res.ok) {
    throw new Error(`Permission fetch failed: ${res.status} ${res.statusText}`);
  }

  const data = await res.json();
  return data.permissions as EffectivePermissions;
}

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn, userInfo } = useFusionAuth() as any;
  const [permissions, setPermissions] = useState<EffectivePermissions>({});
  const [loading, setLoading] = useState(true);

  const isAdmin = useMemo(() => {
    if (isDevMode()) return true;
    const roles = userInfo?.roles;
    if (!Array.isArray(roles)) return false;
    // Match common admin role names — FA / Drupal / downstream may use
    // any of these. Keep the check forgiving so admin bypass works
    // regardless of which naming convention a given env uses.
    return roles.some((r: unknown) => {
      if (typeof r !== 'string') return false;
      const name = r.toLowerCase();
      return name === 'admin'
        || name === 'administrator'
        || name === 'superadmin'
        || name === 'super_admin'
        || name === 'super-admin';
    });
  }, [userInfo]);

  useEffect(() => {
    // Dev mode: grant all operations without fetching
    if (isDevMode()) {
      setPermissions({ __dev_mode__: ['all'] });
      setLoading(false);
      return;
    }

    if (!isLoggedIn) {
      setPermissions({});
      setLoading(true);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const effective = await fetchEffectivePermissions();
        if (!cancelled) {
          setPermissions(effective);
        }
      } catch (err) {
        console.error('Failed to load effective permissions:', err);
        if (!cancelled) {
          setPermissions({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const canDo = useCallback(
    (feature: string, operation: string): boolean => {
      // Dev mode: everything is allowed
      if (permissions.__dev_mode__) {
        return true;
      }
      // Admin gets no implicit short-circuit — admin permissions come from
      // the permission_operation_mappings table just like every other role.
      // Keeps frontend canDo() in lockstep with the backend requireOperation
      // gates instead of letting the UI render things the API will 403.
      const ops = permissions[feature];
      return !!ops && ops.includes(operation);
    },
    [permissions],
  );

  const value = useMemo<PermissionContextValue>(
    () => ({ canDo, isAdmin, permissions, loading }),
    [canDo, isAdmin, permissions, loading],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

/**
 * Access the operation-based permission system.
 *
 * Must be called inside <PermissionProvider>.
 *
 * @returns { canDo, permissions, loading }
 */
export function usePermissions(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return ctx;
}
